/**
 * Pure helper for the /login proactive magic-link cooldown.
 *
 * GoTrue enforces a 60s per-email interval on magic-link sends; this helper
 * computes the client-side advisory countdown so the user never blind-hits
 * the 429. Kept pure (no React / window / localStorage / supabase) so the
 * unit suite stays framework-free — Login.tsx owns all the impure I/O.
 *
 * Run: cd webapp && npx vitest run src/__tests__/loginCooldown.test.ts
 */

/** GoTrue's per-email magic-link interval. */
export const COOLDOWN_MS = 60_000

/**
 * Milliseconds remaining before another magic-link send is allowed.
 * `now` is injected for deterministic testing — never call Date.now() here.
 *
 * WR-03: this pure helper owns input validation. A corrupt localStorage value
 * (manual tampering, an older format, a key collision) yields `Number(raw)` →
 * `NaN`, which is not `null` — so the bare `== null` check used to fall through
 * and propagate `NaN` into the component state (erratic tick interval, no clean
 * clear). `Number.isFinite` rejects NaN/±Infinity at the boundary → fail to 0.
 */
export function remainingCooldownMs(lastSentAt: number | null, now: number): number {
  if (lastSentAt == null || !Number.isFinite(lastSentAt)) return 0
  return Math.max(0, COOLDOWN_MS - (now - lastSentAt))
}

/**
 * localStorage key for the last-send timestamp, scoped per-email.
 * Per-email (not global) matches GoTrue's per-email server-side block.
 */
export function cooldownStorageKey(email: string): string {
  return `ss_login_cooldown:${email.trim().toLowerCase()}`
}
